import React, { FunctionComponent, memo, useCallback, useEffect, useRef, useState } from 'react'
import styles from './ProjectEdit.module.sass'
import Label, { Display } from '../../../controls/Label/Label'
import { /*concat,*/ mergeObject, unit } from '../../../../utils/utils'
import { entityEqPredicate, entityIdPredicate, getId, isOld } from '../../../../utils/entity-utils'
import { parseDate, toServerDate } from '../../../../utils/date-utils'
import { useParams } from 'react-router'
import { User, UserWithDepartments } from '../../../../responses/users'
import DateInput from '../../../data-components/date/DateInput/DateInput'
import Field from '../../../controls/Field/Field'
import Button, { ButtonStyle } from '../../../controls/Button/Button'
import { EMPTY_PROJECT, Project, ServerProject } from '../../../../responses/projects'
import { Brand, ClientWithBrands, Country, Network } from '../../../../responses/refs/refs'
import { Link } from 'react-router-dom'
import { ROUTES } from '../../../../http/routes'
import { RequestStatus } from '../../../../responses/response'
import { urlWithParams } from '../../../../http/utils'
import { useNavigation, useRootDispatch, useRootSelector, useUpload } from '../../../../utils/hooks'
import { Entity } from '../../../../types/UtilityTypes'
import { apiRequests } from '../../../../http/config'
import { EMPTY } from 'rxjs'
import { PERMISSIONS } from '../../../../constants/permissions'
import { statusContext } from '../../../../constants/contexts'
import { ProjectWorkObject } from '../../../../responses/objects'
import { objectsSelector, pipeSelectors, projectObjectsSelector, projectSelector } from '../../../../utils/selectors'
import { useSetState, useShallowCompareEffect } from 'react-use'
import { projectActions } from '../../../../redux/data/project/project-actions'
import Helmet from 'react-helmet'
import Spacer from '../../../etc/Spacer'
import ParticipantsPicker from '../../../data-components/pickers/ParticipantsPicker/ParticipantsPicker'
import DeleteIcon from 'files/delete.svg'
import { isTempFileData } from '../../../../responses/files'
import MaxLength from '../../../controls/MaxLength/MaxLength'
import QuillEditor from '../../../etc/Editor/QuillEditor'
import { Colors } from '../../../../styles'
import { HasAllPermissions } from '../../../etc/HasPermission'
import { navigationActions } from '../../../../redux/global/navigation'
import { isArchivedProject } from '../ProjectPage/ProjectPage'
import Dropdown from '../../../data-components/dropdowns/Dropdown/Dropdown'
import RequestDropdown from '../../../data-components/dropdowns/RequestDropdown/RequestDropdown'

const ProjectEdit: FunctionComponent = () => {
    const [project, setProject] = useSetState<Partial<Project>>({})
    const [objects, setObjects] = useState<ProjectWorkObject[]>([])
    const {projectId}: { projectId: string | undefined } = useParams<{ projectId: string }>()

    const dispatch = useRootDispatch()
    const [brands, setBrands] = useState<Brand[]>([])
    const [brandsSearch, setBrandsSearch] = useState('')
    const user = useRootSelector(state => state.global.user.data)
    const userId = user?.id
    const userDirectionId = user?.direction_id
    const stateProject = useRootSelector(projectSelector(projectId)) || EMPTY_PROJECT
    const projectWorkObjects = useRootSelector(pipeSelectors(projectId, projectObjectsSelector, objectsSelector))

    useShallowCompareEffect(() => setObjects(projectWorkObjects), [projectWorkObjects])

    const {getStatus, updateStatus} = useRootSelector(rootState => {
        const state = rootState.pages.projectEdit
        return {
            getStatus: state.getStatus,
            updateStatus: state.updateStatus,
        }
    })

    const mainRef = useRef(null)
    useNavigation(mainRef)

    const upload = useUpload(project.files)
    const {files} = upload

    useEffect(() => {
        if (projectId && !project.id && isOld(stateProject)) {
            setProject(stateProject)
            setBrands(stateProject.client.brands)
        }
    }, [projectId, setProject, stateProject, project.id])

    useEffect(() => {
        if(isArchivedProject(stateProject.status_id)) {
            dispatch(navigationActions.goBack(ROUTES.ROOT))
        }
    }, [dispatch, stateProject])

    useEffect(function () {
        let dictionarySub = EMPTY.subscribe()
        let managerSub = EMPTY.subscribe()
        if (projectId) {
            dispatch(projectActions.get.request(projectId))
        } else {
            // set direction
            dictionarySub = apiRequests.dictionary.list({
                params: {key: 'direction'},
                query: {id: userDirectionId},
            }).subscribe(
                res => {
                    res.response.success && setProject({
                        direction: res.response.rows.find(entityIdPredicate(userDirectionId))
                    })
                }
            )
            // set manager
            managerSub = apiRequests.dictionary.list({
                params: {key: 'manager'},
                query: {id: userId},
            }).subscribe(
                res => {
                    res.response.success && setProject(mergeObject<Partial<ServerProject>>({
                        manager: res.response.rows.find(entityIdPredicate<User>(userId))
                    }))
                }
            )
            // clear project
            setProject({
                opened_at: toServerDate(new Date()),
            })
        }
        return () => {
            dictionarySub.unsubscribe()
            managerSub.unsubscribe()
        }
    }, [dispatch, projectId, setProject, userId, userDirectionId])

    const onSuccess = () => {
        const changes: Partial<ServerProject> & Entity = {
            ...project,
            id: projectId,
            //files: concat(files.filter(isTempFileData).map(temp => temp.data))(project.files || []),
            files: files.filter(isTempFileData).map(temp => temp.data),
            project_work_objects: projectWorkObjects.concat(objects),
        }
        dispatch(projectId ?
            projectActions.update.request(changes, {redirect: true}) :
            projectActions.create.request(changes))
    }

    const setUsers = useCallback(((users: UserWithDepartments[]) => setProject({users})), [setProject])
    const descriptionChange = useCallback((description: string) => setProject({description}), [setProject])

    return <div className={styles.page}>
        <Helmet>
            <title>{projectId ? `${project.code} - Редактирование` : 'Новый проект'}</title>
        </Helmet>
        <statusContext.Provider value={getStatus}>
            <div ref={mainRef} className={styles.main}>
                <div className={styles.content}>
                    <h1>{projectId ? 'Редактирование проекта' : 'Новый проект'}</h1>
                    <Label
                        labelClassName={styles.firstColumn}
                        label={'Направление *'}
                        display={Display.CONTENTS}
                    >
                        <RequestDropdown
                            type={'single'}
                            value={project.direction}
                            render={direction => direction.title}
                            onChange={direction => setProject({direction})}
                            request={query => apiRequests.dictionary.list({
                                params: {key: 'direction'},
                                query
                            })}
                        />
                    </Label>

                    <Label
                        labelClassName={styles.firstColumn}
                        label={'Менеджер *'}
                        display={Display.CONTENTS}
                    >
                        <RequestDropdown
                            type={'single'}
                            value={project.manager}
                            render={manager => manager.name}
                            onChange={manager => setProject({manager})}
                            request={query => apiRequests.dictionary.list({params: {key: 'manager'}, query})}
                        />
                    </Label>

                    <Label
                        labelClassName={styles.firstColumn}
                        label={'Клиент *'}
                        display={Display.CONTENTS}
                    >
                        <RequestDropdown<ClientWithBrands>
                            type={'single'}
                            value={project.client as ClientWithBrands}
                            render={client => client.short_title}
                            request={query => apiRequests.dictionary.list({params: {key: 'client'}, query})}
                            onChange={client => {
                                const brands = client?.brands ?? []
                                setBrands(brands)
                                setProject({
                                    client,
                                    brand: brands.find(entityEqPredicate(project.brand)) ?? undefined
                                })
                            }}
                        />
                    </Label>

                    <Label
                        labelClassName={styles.firstColumn}
                        label={'Бренд *'}
                        display={Display.CONTENTS}
                    >
                        <Dropdown<Brand>
                            data={brands.filter(brand => brand.title.toLowerCase().includes(brandsSearch))}
                            toKey={getId}
                            selected={unit(project.brand)}
                            render={brand => brand.title}
                            onClear={() => setProject({brand: undefined})}
                            onChange={setBrandsSearch}
                            onClick={brand => setProject({brand})}
                        />
                    </Label>

                    <Label
                        labelClassName={styles.firstColumn}
                        label={'Дата открытия *'}
                        display={Display.CONTENTS}
                    >
                        <DateInput
                            value={parseDate(project.opened_at)}
                            onChange={date => setProject({opened_at: toServerDate(date)})}
                        />
                    </Label>

                    <Label
                        labelClassName={styles.firstColumn}
                        label={<MaxLength max={40} value={project.short_name}>Название *</MaxLength>}
                        display={Display.CONTENTS}
                    >
                        <Field
                            block
                            type={'text'}
                            maxLength={40}
                            className={styles.doubleColumn}
                            value={project.short_name}
                            onChange={short_name => setProject({short_name})}
                        />
                    </Label>

                    <Label
                        labelClassName={styles.firstColumn}
                        label={'Worksection'}
                        display={Display.CONTENTS}
                    >
                        <Field
                            block
                            type={'text'}
                            className={styles.doubleColumn}
                            value={project.worksection}
                            onChange={worksection => setProject({worksection})}
                        />
                    </Label>

                    <Label
                        labelClassName={styles.firstColumn}
                        label={'Тип *'}
                        display={Display.CONTENTS}
                    >
                        <RequestDropdown
                            type={'nullable'}
                            empty={'<Не задан>'}
                            value={project.work_type ?? null}
                            render={type => type.code + ' - ' + type.name}
                            onChange={work_type => setProject({work_type})}
                            request={query => apiRequests.dictionary.list({
                                params: {key: 'work-type'},
                                query
                            })}
                        />
                    </Label>

                    <Label
                        labelClassName={styles.firstColumn}
                        label={'Сеть/издание'}
                        display={Display.CONTENTS}
                    >
                        <RequestDropdown<Network>
                            type={'nullable'}
                            empty={'<Не задана>'}
                            value={project.network ?? null}
                            render={network => network.name}
                            onChange={network => setProject({network})}
                            request={query => apiRequests.dictionary.list({params: {key: 'network'}, query})}
                        />
                    </Label>

                    <Label
                        labelClassName={styles.firstColumn}
                        label={'Страна'}
                        display={Display.CONTENTS}
                    >
                        <RequestDropdown<Country>
                            type={'multi'}
                            value={project.countries ?? []}
                            render={country => country.code}
                            request={query => apiRequests.dictionary.list({
                                params: {key: 'country'},
                                query
                            })}
                            onChange={countries => setProject({countries})}
                        />
                    </Label>

                    <h2>Бриф / описание проекта</h2>

                    <Label
                        labelClassName={styles.firstColumn}
                        contentClassName={styles.doubleColumn}
                        label={'Описание проекта'}
                        display={Display.CONTENTS}
                    >
                        <QuillEditor
                            upload={upload}
                            value={project.description}
                            onChange={descriptionChange}
                        />
                    </Label>
                    <div className={styles.spacer}/>
                </div>

                {
                    <div className={styles.actions}>
                        <HasAllPermissions
                            condition={!!projectId}
                            permissions={[PERMISSIONS.PROJECT.DELETE]}
                        >
                            <Button
                                color={Colors.red}
                                onClick={() => dispatch(projectActions.startDelete(stateProject))}
                            >
                                <DeleteIcon/> Удалить
                            </Button>
                        </HasAllPermissions>
                        <Spacer/>
                        <Link to={projectId ? urlWithParams({projectId})(ROUTES.PROJECT_DETAIL) : ROUTES.PROJECTS}>
                            <Button>Отмена</Button>
                        </Link>
                        <Button
                            className={styles.saveButton}
                            color={Colors.blue}
                            buttonStyle={ButtonStyle.BORDERED}
                            disabled={updateStatus === RequestStatus.FETCHING}
                            onClick={onSuccess}
                        >
                            {projectId ? 'Сохранить' : 'Открыть'}
                        </Button>
                    </div>
                }
            </div>

            <ParticipantsPicker
                value={project.users}
                onChange={setUsers}
            />
        </statusContext.Provider>
    </div>
}

export default memo(ProjectEdit)